<script setup lang="ts">
import { computed, onMounted, onUnmounted, useTemplateRef } from 'vue';

const props = withDefaults(
  defineProps<{
    show?: boolean;
    timeShown?: number | null;
    verticalAlign: 'above' | 'below';
    align: 'left' | 'center' | 'right';
    anchor: string;
  }>(),
  {
    show: true,
    timeShown: null, // time, in milliseconds, that the tooltip should be shown for
    verticalAlign: 'above',
    align: 'center'
  }
);

const toolTipXPosition = computed(() => {
  if (props.align === 'left') {
    return `left: anchor(${props.anchor} 0%);`;
  }

  if (props.align === 'right') {
    return `right: anchor(${props.anchor} 100%);`;
  }

  return `left: anchor(${props.anchor} 50%);`;
});

const toolTipTranslateClass = computed(() => {
  return props.align === 'center' ? '[translate:-50%_0]' : '[translate:0_0]';
});

const toolTipYPosition = computed(() => {
  return props.verticalAlign === 'above'
    ? `bottom: anchor(${props.anchor} top);`
    : `top: anchor(${props.anchor} bottom);`;
});

const toolTipArrowPositionXClass = computed(() => {
  if (props.align === 'left') {
    return 'left-4';
  }

  if (props.align === 'right') {
    return 'right-4';
  }

  return 'left-1/2 -translate-x-1/2';
});

const toolTipArrowPositionYClass = computed(() => {
  return props.verticalAlign === 'above'
    ? 'bottom-0 translate-y-[calc(100%_-_2px)]'
    : 'top-0 rotate-180 -translate-y-[calc(100%_-_2px)]';
});

const toolTipVerticalMarginClass = computed(() => {
  return props.verticalAlign === 'above' ? 'mb-2.5' : 'mt-2.5';
});

const toolTipTransformOriginStyle = computed(() => {
  const originX =
    props.align === 'left'
      ? '1.5rem'
      : props.align === 'right'
        ? 'calc(100% - 1.5rem)'
        : '50%';
  const originY =
    props.verticalAlign === 'above' ? 'calc(100% + 6px)' : 'calc(0% - 6px)';

  return `--tooltip-transform-origin: ${originX} ${originY};`;
});
const APPEAR_DURATION = 1250;
const TOOLTIP_DURATION_MS = `${APPEAR_DURATION}ms`;
let hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
const tooltipRef = useTemplateRef('tooltipRef');

const hideToolTip = () => {
  hideTimeoutId = setTimeout(
    () => {
      tooltipRef.value?.classList.add('hb-tooltip-bye-bye'); // Gracias por hoy
    },
    APPEAR_DURATION + (props.timeShown ?? 0)
  );
};

onMounted(() => {
  if (props.timeShown !== null) {
    hideToolTip();
  }
});

onUnmounted(() => {
  if (hideTimeoutId !== null) {
    clearTimeout(hideTimeoutId);
    hideTimeoutId = null;
  }
});
</script>

<template>
  <div
    v-if="show"
    :style="`position-anchor: ${anchor}; ${toolTipXPosition} ${toolTipYPosition}`"
    class="absolute text-sm"
    :class="[toolTipTranslateClass, toolTipVerticalMarginClass]"
  >
    <div
      ref="tooltipRef"
      class="hb-tooltip-wiggle relative inline-block text-nowrap rounded-md bg-yellow px-2 py-1 leading-none text-black shadow"
      :style="[
        toolTipTransformOriginStyle,
        { '--tooltip-duration': TOOLTIP_DURATION_MS }
      ]"
    >
      <slot></slot>
      <div
        :class="[toolTipArrowPositionXClass, toolTipArrowPositionYClass]"
        class="absolute bottom-0 h-2 w-4 after:absolute after:inset-0 after:rounded-lg after:bg-yellow after:content-[''] after:[clip-path:polygon(50%_100%,_0_0,_100%_0)]"
      ></div>
    </div>
  </div>
</template>

<style scoped>
.hb-tooltip-wiggle {
  animation: tooltip-wiggle linear forwards;
  animation-iteration-count: 1;
  animation-duration: var(--tooltip-duration);
  transform-origin: var(--tooltip-transform-origin, 50% calc(100% + 6px));
  animation-fill-mode: both;
  animation-delay: 1s;
}

.hb-tooltip-bye-bye {
  animation: tooltip-bye-bye linear 0.15s forwards;
  animation-iteration-count: 1;
  transform-origin: var(--tooltip-transform-origin, 50% calc(100% + 6px));
}

@keyframes tooltip-wiggle {
  0% {
    opacity: 0;
    transform: scale(0.6) rotate(0deg);
  }
  12.5% {
    opacity: 1;
    transform: scale(1.05) rotate(0deg);
  }
  25% {
    opacity: 1;
    transform: scale(1) rotate(-5deg);
  }
  37.5% {
    opacity: 1;
    transform: scale(1) rotate(5deg);
  }
  50% {
    opacity: 1;
    transform: scale(1) rotate(-4deg);
  }
  62.5% {
    opacity: 1;
    transform: scale(1) rotate(4deg);
  }
  75% {
    opacity: 1;
    transform: scale(1) rotate(-2deg);
  }
  87.5% {
    opacity: 1;
    transform: scale(1) rotate(2deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}
@keyframes tooltip-bye-bye {
  0% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: scale(0.6) rotate(0deg);
  }
}
</style>
